import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {SourceLinkView} from '../../api';
import {BackgroundProgress} from '../background-progress.model';
import {SourceLinkSyncProgressEvent} from '../sse/sse-event.model';
import {StateConfigService} from '../state/state-config.service';

/** Stand eines Abgleichs, wie ihn Popup, Ordner-Button und Hintergrund-Reiter zeigen. */
export interface SourceLinkSyncState {
  linkId: string;
  projectId: number;
  /** Letzte Fortschrittsmeldung; fehlt, bis die erste ankommt. */
  progress?: SourceLinkSyncProgressEvent;
  /** Gesetzt, sobald der Abgleich durch ist: der Link mit Ergebnis (Status, Fehler, Dateizahl). */
  finished?: SourceLinkView;
  startedAt: number;
  updatedAt: number;
}

/**
 * Ohne Meldung so lange gilt ein Abgleich als verwaist. Fortschritt kommt bei jeder Datei; länger still ist es nur,
 * während eine einzelne sehr große Datei übertragen wird. Fehlt das Abschlussereignis (SSE-Lücke), soll der Reiter
 * trotzdem nicht für die ganze Sitzung stehen bleiben.
 */
const STALE_AFTER_MS = 10 * 60 * 1000;

/**
 * Laufende Abgleiche verknüpfter Ordner (BIC-Frontend#2679), gespeist aus `SOURCE_LINK_SYNC_PROGRESS` und
 * `SOURCE_LINK_SYNCED`.
 *
 * Der Abgleich läuft rein serverseitig. Dieser Dienst sammelt nur die Meldungen, damit das Fortschritts-Popup,
 * der Button im verknüpften Ordner und der Hintergrund-Reiter dasselbe zeigen — auch für Abgleiche, die der
 * Takt oder ein anderes Projektmitglied angestoßen hat.
 */
@Injectable({providedIn: 'root'})
export class SourceLinkSyncService {
  private readonly stateConfigService = inject(StateConfigService);
  private readonly statesSubject = new BehaviorSubject<Map<string, SourceLinkSyncState>>(new Map());

  /** Laufende Abgleiche für die Hintergrund-Badge, ein Eintrag je Projekt. */
  public readonly progressByProject$: Observable<BackgroundProgress[]> = this.statesSubject.pipe(
    map(states => this.toBackgroundProgress(states)),
  );

  constructor() {
    this.stateConfigService.sseEvents$.subscribe(event => {
      if (event.type === 'SOURCE_LINK_SYNC_PROGRESS') {
        this.onProgress(event.data as SourceLinkSyncProgressEvent);
      } else if (event.type === 'SOURCE_LINK_SYNCED') {
        this.finish(event.data as SourceLinkView);
      }
    });
  }

  state$(linkId: string): Observable<SourceLinkSyncState | undefined> {
    return this.statesSubject.pipe(map(states => states.get(linkId)), distinctUntilChanged());
  }

  state(linkId: string | undefined): SourceLinkSyncState | undefined {
    return linkId ? this.statesSubject.value.get(linkId) : undefined;
  }

  /**
   * Ein Abgleich wurde angestoßen (Ordner verknüpft oder „Jetzt synchronisieren“). Bis der Takt ihn aufgreift,
   * vergehen bis zu 30 s ohne Meldung; das Popup zeigt in der Zeit schon „wird gelesen“ statt nichts.
   */
  started(link: SourceLinkView): void {
    if (!link.id || link.projectId == null) {
      return;
    }
    const current = this.statesSubject.value.get(link.id);
    if (current && !current.finished) {
      return;
    }
    const now = Date.now();
    this.put({linkId: link.id, projectId: link.projectId, startedAt: now, updatedAt: now});
  }

  /**
   * Neuer Stand des Links aus der REST-API. Holt das Ende nach, wenn das Abschlussereignis verloren ging: der Link
   * läuft nicht mehr und wurde nach dem Start abgeglichen.
   */
  linkLoaded(link: SourceLinkView): void {
    const current = this.state(link.id);
    if (!current || current.finished || link.syncing || !link.lastSyncAt) {
      return;
    }
    if (new Date(link.lastSyncAt).getTime() >= current.startedAt) {
      this.finish(link);
    }
  }

  /** Popup geschlossen: das fertige Ergebnis wird nicht mehr gebraucht. Laufende Abgleiche bleiben. */
  dismiss(linkId: string): void {
    const current = this.statesSubject.value.get(linkId);
    if (current?.finished) {
      const next = new Map(this.statesSubject.value);
      next.delete(linkId);
      this.statesSubject.next(next);
    }
  }

  private onProgress(progress: SourceLinkSyncProgressEvent): void {
    if (!progress?.linkId) {
      return;
    }
    const now = Date.now();
    const current = this.statesSubject.value.get(progress.linkId);
    // Ein neuer Lauf nach einem fertigen: frisch beginnen, das alte Ergebnis gehört nicht dazu.
    const startedAt = current && !current.finished ? current.startedAt : now;
    this.put({linkId: progress.linkId, projectId: progress.projectId, progress, startedAt, updatedAt: now});
  }

  private finish(link: SourceLinkView): void {
    if (!link?.id) {
      return;
    }
    const current = this.statesSubject.value.get(link.id);
    if (!current) {
      // Nie gesehen (weder angestoßen noch Fortschritt): niemand wartet darauf.
      return;
    }
    this.put({...current, finished: link, updatedAt: Date.now()});
  }

  private put(state: SourceLinkSyncState): void {
    const next = new Map(this.statesSubject.value);
    next.set(state.linkId, state);
    this.statesSubject.next(next);
  }

  private toBackgroundProgress(states: Map<string, SourceLinkSyncState>): BackgroundProgress[] {
    const now = Date.now();
    const byProject = new Map<number, BackgroundProgress>();
    states.forEach(state => {
      if (state.finished || now - state.updatedAt > STALE_AFTER_MS) {
        return;
      }
      const total = state.progress?.filesTotal ?? 0;
      const done = state.progress?.filesDone ?? 0;
      const bucket = byProject.get(state.projectId) ?? {
        total: 0, done: 0, failed: 0, active: 0, currentNames: [], failedNames: [], projectId: state.projectId,
      };
      bucket.total += total;
      // `failed` bleibt 0: filesDone zählt gescheiterte Dateien schon mit, die Badge rechnet done + failed. Fehler
      // zeigen das Popup und der Ordner-Button.
      bucket.done += done;
      // Mindestens 1, solange der Lauf nicht fertig ist: beim Lesen der Quelle steht die Gesamtzahl noch nicht fest,
      // der Abgleich läuft aber schon.
      bucket.active += Math.max(1, total - done);
      if (state.progress?.currentName) {
        bucket.currentNames.push(state.progress.currentName);
      }
      byProject.set(state.projectId, bucket);
    });
    return Array.from(byProject.values());
  }
}
